import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';

import { formatApiMessage } from '@/api/client';
import { usersApi } from '@/api/users.api';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/stores/auth.store';

const schema = z.object({
  firstName: z.string().min(1, 'Required').max(100),
  lastName: z.string().min(1, 'Required').max(100),
  phone: z.string().max(30).optional().or(z.literal('')),
  department: z.string().max(100).optional().or(z.literal('')),
  jobTitle: z.string().max(100).optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phone: '',
      department: '',
      jobTitle: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!user) throw new Error('Not signed in');
      return usersApi.update(user.id, {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || undefined,
        department: values.department || undefined,
        jobTitle: values.jobTitle || undefined,
      });
    },
    onSuccess: (updated) => {
      if (user) {
        setUser({ ...user, firstName: updated.firstName, lastName: updated.lastName });
      }
      reset({
        firstName: updated.firstName,
        lastName: updated.lastName,
        phone: '',
        department: updated.department ?? '',
        jobTitle: updated.jobTitle ?? '',
      });
      toast.success(t('settings_profile.saved'));
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_profile.title')}</h1>
        <p className="page-subtitle">{t('settings_profile.subtitle')}</p>
      </div>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="card max-w-2xl space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t('settings_profile.first_name')}</label>
            <input className="input" {...register('firstName')} />
            {errors.firstName && <p className="text-xs text-danger mt-1">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="label">{t('settings_profile.last_name')}</label>
            <input className="input" {...register('lastName')} />
            {errors.lastName && <p className="text-xs text-danger mt-1">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label className="label">{t('settings_profile.email')}</label>
          <input className="input" value={user?.email ?? ''} disabled />
          <p className="help">{t('settings_profile.email_change_hint')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t('settings_profile.phone')}</label>
            <input className="input" placeholder="+994 ..." {...register('phone')} />
          </div>
          <div>
            <label className="label">{t('settings_profile.job_title')}</label>
            <input className="input" placeholder={t('settings_profile.job_title_placeholder')} {...register('jobTitle')} />
          </div>
        </div>

        <div>
          <label className="label">{t('settings_profile.dept')}</label>
          <input className="input" placeholder={t('settings_profile.dept_placeholder')} {...register('department')} />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-xs text-muted">
            {t('common.role')}: <strong className="text-gray-900">{t(`settings_team.role_${user?.role}`)}</strong>
            {' · '}
            {t('settings_profile.org')}: <strong className="text-gray-900">{user?.organization.name}</strong>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isDirty || mutation.isPending}
          >
            {mutation.isPending ? t('common.saving') : t('settings_profile.save_changes')}
          </button>
        </div>
      </form>
    </AppShell>
  );
}
